import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { TopLayer } from '@app/core/layers/top-layer';
import { LoadingService } from '@app/core/loading/loading.service';

/**
 * Overlay de carga global. Es un `<dialog>` modal para quedar en el top layer: tapa también un diálogo que ya esté
 * abierto (ej. el confirm de borrado), deja inerte todo lo demás sin quitarle el foco a nadie y, al cerrarse,
 * devuelve el foco a donde estaba.
 */
@Component({
  selector: 'app-loading-overlay',
  templateUrl: './loading-overlay.html',
  styleUrl: './loading-overlay.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-visible]': 'isVisible() || null',
  },
})
export class LoadingOverlay {
  private readonly document = inject(DOCUMENT);
  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  private readonly topLayer = inject(TopLayer);
  protected readonly isVisible = inject(LoadingService).isLoading;

  constructor() {
    effect(() => {
      const dialog = this.dialog().nativeElement;
      if (this.isVisible()) {
        this.show(dialog);
      } else {
        this.hide(dialog);
      }
    });
  }

  // Se mira el atributo y no `dialog.open`: sin soporte de <dialog> esa propiedad no existe.
  private show(dialog: HTMLDialogElement): void {
    if (dialog.hasAttribute('open')) {
      return;
    }
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Sin <dialog> tampoco hay `inert`: queda como una capa fija que tapa la página.
      dialog.setAttribute('open', '');
    }
    // TopLayer la mantiene encima de cualquier modal y le pone adentro los avisos.
    this.topLayer.opened(dialog, 'loading');
  }

  private hide(dialog: HTMLDialogElement): void {
    if (!dialog.hasAttribute('open')) {
      return;
    }
    // El foco se suelta antes de cerrar, mientras el diálogo todavía es enfocable (cerrado, `blur()` ya no lo mueve).
    // Al cerrarse, un <dialog> modal igual devuelve el foco a donde estaba; pero si ese nodo ya no existe (ej. la
    // fila borrada), sin esto el foco quedaría en el diálogo oculto en vez de caer al <body>, que es lo que esperan
    // RouteFocus y las páginas.
    const active = this.document.activeElement;
    if (active instanceof HTMLElement && dialog.contains(active)) {
      active.blur();
    }
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  /** Esc no corta la carga. Donde existe `closedby="none"` ni llega; esto cubre a Safari. */
  protected onCancel(event: Event): void {
    event.preventDefault();
  }

  /** Safari deja pasar un Esc repetido aunque se cancele: si se cerró con la carga en curso, vuelve a abrirse. */
  protected onClose(): void {
    if (this.isVisible()) {
      this.show(this.dialog().nativeElement);
    }
  }
}
